/*
 * fibbl-gallery-sync
 *
 * The product media gallery optimistically renders a Fibbl 3D slide (and a
 * matching thumbnail) for every product. This component confirms — via
 * Fibbl's documented `Fibbl.checkModelAvailability()` API — whether the
 * product actually has a 3D model. If it does not, it hides the slide and
 * notifies the surrounding <media-gallery> (bubbling `fibbl:unavailable`
 * event) so the empty slide is removed from the Swiper. Without that, an empty
 * 3D slide would collapse the mobile gallery height via Swiper's autoHeight.
 *
 * Only documented Fibbl APIs are used (no internal DOM/attribute sniffing).
 * Docs: https://docs.fibbl.com/programmatic-api.html
 */

if (!customElements.get('fibbl-gallery-sync')) {
  customElements.define(
    'fibbl-gallery-sync',
    class FibblGallerySync extends HTMLElement {
      static FIBBL_LOAD_TIMEOUT = 8000;

      abortController;

      async connectedCallback() {
        // Swiper's loop mode (v8) clones the first slide into a duplicate
        // (.swiper-slide-duplicate). Strip the cloned viewer so the 3D model
        // isn't loaded a second time — the real slide handles availability
        // and slide removal.
        if (this.closest('.swiper-slide-duplicate')) {
          this.querySelector('fibbl-model-viewer')?.remove();
          return;
        }

        this.abortController = new AbortController();

        this.viewer = this.querySelector('fibbl-model-viewer');
        this.productId = this.viewer?.dataset.productId;
        if (!this.productId) return;

        this.gallery = this.closest('media-gallery');
        this.slide = this.closest('.product__media-item');
        this.thumbnail = this.gallery?.querySelector('.product__thumbs-item[data-target="fibbl"]');

        const available = await this.isModelAvailable();
        if (this.abortController.signal.aborted) return;

        if (!available) this.markUnavailable();
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      // Documented availability API. Resolves false if Fibbl fails to load
      // (CDN blocked / non-registered origin) or the check throws.
      async isModelAvailable() {
        const fibbl = await this.waitForFibbl();
        if (!fibbl || typeof fibbl.checkModelAvailability !== 'function') return false;

        try {
          return await fibbl.checkModelAvailability(this.productId);
        } catch (error) {
          return false;
        }
      }

      // window.Fibbl is assigned once the async ES module finishes loading.
      waitForFibbl() {
        return new Promise((resolve) => {
          if (window.Fibbl) {
            resolve(window.Fibbl);
            return;
          }

          let interval;
          let timeout;
          const cleanup = () => {
            clearInterval(interval);
            clearTimeout(timeout);
          };

          interval = setInterval(() => {
            if (window.Fibbl) {
              cleanup();
              resolve(window.Fibbl);
            }
          }, 100);

          timeout = setTimeout(() => {
            cleanup();
            resolve(null);
          }, FibblGallerySync.FIBBL_LOAD_TIMEOUT);

          this.abortController.signal.addEventListener('abort', () => {
            cleanup();
            resolve(null);
          });
        });
      }

      markUnavailable() {
        this.slide?.classList.add('is-fibbl-unavailable');
        this.thumbnail?.classList.add('is-fibbl-unavailable');

        // Let <media-gallery> drop the empty slide from the Swiper so the
        // mobile gallery height (autoHeight) doesn't collapse.
        this.dispatchEvent(new CustomEvent('fibbl:unavailable', { bubbles: true }));
      }
    }
  );
}
